import { useRepository } from 'hooks/auth'
import { useObjectUrlCacheManager } from 'hooks/objectUrl'
import { useEffect, useState } from 'react'
import { captureException } from '@sentry/react'
import { observer } from 'mobx-react-lite'
import { StorageFetchError } from '@breakoutlearning/firebase-repository/types'
import {
  MeetingTelemetryEvent,
  errorName,
  sanitizeErrorMessage,
} from '@breakoutlearning/firebase-repository/util'
import {
  canSendFirebaseToken,
  emulatorStorageOrigins,
  isSignedUrl,
} from 'util/storageUrl'

// Host of the asset URL only, never the full signed URL (which can carry
// tokens). Enough to tell one CDN/origin from another when an image won't load.
function assetHost(url: string | undefined): string | undefined {
  if (!url) return undefined
  try {
    return new URL(url, window.location.href).host
  } catch {
    return undefined
  }
}

type GoogleStorageImageProps = {
  src?: string
  alt: string
  loader?: React.ReactNode
  namespace: string
  decoding?: 'sync' | 'async' | 'auto'
} & React.HTMLAttributes<HTMLImageElement>

export const GoogleStorageImage = observer(function GoogleStorageImage({
  src,
  namespace,
  loader,
  alt,
  ...rest
}: GoogleStorageImageProps) {
  const repository = useRepository()
  const authToken = repository.authToken
  // Content-image telemetry is scoped to meeting content (exhibits, image
  // slides), the analog to the deck-video events — not every avatar.
  const emitTelemetry = namespace === 'meeting'
  const objectUrlCache = useObjectUrlCacheManager()
  const initialObjectUrl = objectUrlCache.get(namespace, src)
  const [objectUrl, setObjectUrl] = useState<string | undefined>(
    initialObjectUrl
  )
  const [retries, setRetries] = useState(0)

  useEffect(() => {
    if (!src || !authToken) return
    if (objectUrl) return
    if (retries >= 3) return

    // Signed URLs from the API are already authorized — no auth header needed.
    // Unsigned storage URLs (from direct Firestore reads) still need the Bearer
    // token. Any other host loads without it, since its server could read it.
    const shouldSendToken =
      !isSignedUrl(src) && canSendFirebaseToken(src, emulatorStorageOrigins)
    const options = shouldSendToken
      ? {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      : {}

    setTimeout(
      () => {
        // Emit once per source, on the first attempt, not per retry.
        if (emitTelemetry && retries === 0) {
          repository.logEvent(MeetingTelemetryEvent.contentImageLoadAttempted, {
            asset_host: assetHost(src),
          })
        }
        // Captured when a response comes back, so a failure can report the HTTP
        // status. Stays undefined for a network-level failure (fetch rejects
        // with no response) — that case is an error_name like TypeError.
        let httpStatus: number | undefined
        fetch(src, options)
          .then((res) => {
            httpStatus = res.status
            // fetch does not reject on 4xx/5xx; treat a non-ok response as a
            // failure rather than turning the error body into a bogus image.
            if (!res.ok) {
              throw new Error(`HTTP ${res.status} fetching storage image`)
            }
            return res.blob()
          })
          .then((blob) => {
            const newObjectURL = URL.createObjectURL(blob)
            objectUrlCache.set(namespace, src, newObjectURL)
            setObjectUrl(newObjectURL)
            setRetries(0)
            if (emitTelemetry) {
              repository.logEvent(MeetingTelemetryEvent.contentImageLoaded, {
                asset_host: assetHost(src),
                retries,
              })
            }
          })
          .catch((err) => {
            if (retries < 3) {
              setRetries(retries + 1)
            } else {
              if (emitTelemetry) {
                repository.logEvent(
                  MeetingTelemetryEvent.contentImageLoadFailed,
                  {
                    asset_host: assetHost(src),
                    retries,
                    error_code: httpStatus,
                    error_name: errorName(err),
                    error_message_sanitized: sanitizeErrorMessage(err),
                  }
                )
              }
              captureException(
                new StorageFetchError(`Failed to fetch storage image: ${src}`, {
                  cause: err,
                })
              )
              console.error('Failed to fetch image', err)
            }
          })
      },
      1000 * retries * 2 // exponential backoff - 0s, 2s, 4s, 8s
    )
  }, [
    authToken,
    src,
    objectUrlCache,
    namespace,
    objectUrl,
    retries,
    emitTelemetry,
    repository,
  ])

  if (!objectUrl)
    return loader ? (
      <div className="flex h-full w-full items-center justify-center">
        {loader}
      </div>
    ) : null

  return (
    <img
      src={objectUrl}
      onError={() => {
        if (retries < 3) {
          // if the image fails to load, remove the object url from the cache
          // to fetch it again
          // but only if we haven't retried too many times
          if (src) objectUrlCache.remove(namespace, src)
          setObjectUrl(undefined)
          setRetries(retries + 1)
        }
      }}
      alt={alt}
      {...rest}
    />
  )
})
